import { useEffect, useState } from "react";
import { ArrowRight, Award, BookOpen, CheckCircle2, CirclePause, Clock3, FileText, Lightbulb, MessageSquareText, Rocket, RotateCcw, UsersRound, XCircle } from "lucide-react";
import { GROWTH_ACCESS_EMPLOYEES, GROWTH_ALL_REQUESTS, GROWTH_ASSIGNED_REQUESTS, USER_PROFILE } from "../../../config/Endpoints.js";
import { getAuthToken } from "../../../utils/Storage.js";
import { getApi } from "../../utils/api.ts";
import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";
import { useUser } from "../../lib/rbac/useUser.ts";
import { useEmployeeNameOptions } from "../../hooks/use-employee-name-options.ts";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Button } from "../../components/ui/button/button.tsx";
import { Spinner } from "../../components/ui/spinner/spinner.tsx";
import { DateRange } from "../../components/ui/date-range/date-range.tsx";
import { Select } from "../../components/ui/select/select.tsx";
import { TablePagination } from "../../components/ui/table/table-pagination.tsx";
import ManagerRequestDetail from "./manager-request-detail.tsx";
import type { GrowthRequest } from "../growth-activities/growth-types.ts";
import styles from "./team-growth-page.module.css";

const categories = [
  { value: "CONTRIBUTION", label: "Contribute", title: "Interest in a project opportunity", icon: UsersRound, tone: "blue" },
  { value: "LEARNING", label: "Learn & Upskill", title: "Interest in learning and upskilling", icon: BookOpen, tone: "purple" },
  { value: "CERTIFICATION", label: "Certification", title: "Interest in pursuing a certification", icon: Award, tone: "amber" },
  { value: "INNOVATION", label: "Innovate & Improve", title: "An idea to innovate and improve", icon: Lightbulb, tone: "mint" },
  { value: "LAUNCHING", label: "Build & Launch", title: "A solution to build and launch", icon: Rocket, tone: "pink" },
  { value: "KNOWLEDGE_SHARING", label: "Host & Share Knowledge", title: "Interest in hosting and sharing knowledge", icon: MessageSquareText, tone: "indigo" },
];
const statuses = [
  { value: "RECEIVED", label: "Received" }, { value: "BEING_REVIEWED", label: "Being reviewed" },
  { value: "IN_PROGRESS", label: "In progress" }, { value: "WAITING_EMPLOYEE", label: "Waiting for employee" },
  { value: "COMPLETED", label: "Completed" }, { value: "CLOSED", label: "Closed" },
];
const statusCards = [
  { value: "", label: "Total Initiatives", Icon: FileText, tone: "blue" },
  { value: "RECEIVED", label: "Received", Icon: Clock3, tone: "amber" },
  { value: "BEING_REVIEWED", label: "Being reviewed", Icon: BookOpen, tone: "purple" },
  { value: "IN_PROGRESS", label: "In progress", Icon: Rocket, tone: "indigo" },
  { value: "WAITING_EMPLOYEE", label: "Waiting for employee", Icon: CirclePause, tone: "amber" },
  { value: "COMPLETED", label: "Completed", Icon: CheckCircle2, tone: "green" },
  { value: "CLOSED", label: "Closed", Icon: XCircle, tone: "slate" },
] as const;
type Initiative = GrowthRequest & { employeeId: number; employeeName: string; assignedToName?: string | null };
type InitiativePage = { content: Initiative[]; totalPages: number; totalElements: number; statusCounts?: Record<string, number> };
type View = "attention" | "assigned";
const actionLabel = (status: string) => status === "RECEIVED" ? "REVIEW INITIATIVE" : status === "BEING_REVIEWED" ? "RECORD MEANINGFUL PROGRESS" : status === "IN_PROGRESS" ? "SHARE PROGRESS" : "";
const localDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
function selectedDates(range: string, start: Date | null, end: Date | null) {
  if (range === "custom") return start && end ? [localDate(start), localDate(end)] : null;
  if (range !== "7" && range !== "30") return null;
  const today = new Date();
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - Number(range) + 1);
  return [localDate(from), localDate(today)];
}
function sharedDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function GrowthInsightsPage() {
  const roles = useUser()?.roles;
  const isGrowthAdmin = roles?.some((role) => String(role).trim().toLowerCase() === "g&i admin") ?? false;
  return <TeamGrowthPage management={isGrowthAdmin} />;
}

export default function TeamGrowthPage({ management = false }: { management?: boolean }) {
  const { setBreadcrumbs } = useBreadcrumbs();
  const employees = useEmployeeNameOptions();
  const [actorId, setActorId] = useState<number | null>(null);
  const [actorLoaded, setActorLoaded] = useState(false);
  const [tabCounts, setTabCounts] = useState<{ attention: number | null; assigned: number | null }>({ attention: null, assigned: null });
  const [statusCounts, setStatusCounts] = useState<Record<string, number | null>>({});
  const [requests, setRequests] = useState<Initiative[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [assignedRequests, setAssignedRequests] = useState<Initiative[]>([]);
  const [assignedLoading, setAssignedLoading] = useState(true);
  const [assignedError, setAssignedError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [cardRefresh, setCardRefresh] = useState(0);
  const [tab, setTab] = useState<View>(management ? "attention" : "assigned");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState(management ? "RECEIVED" : "");
  const [employee, setEmployee] = useState("");
  const [owner, setOwner] = useState("");
  const [ownerOptions, setOwnerOptions] = useState<{ value: string; label: string }[]>([]);
  const [ownerOptionsError, setOwnerOptionsError] = useState("");
  const [ownerOptionsAttempt, setOwnerOptionsAttempt] = useState(0);
  const [dateRange, setDateRange] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [detailId, setDetailId] = useState<number | null>(null);
  const dates = selectedDates(dateRange, startDate, endDate);
  const fromDate = dates?.[0] || "";
  const toDate = dates?.[1] || "";
  const apiTab = management ? tab : "assigned";
  const ownerActorId = owner === "me" ? actorId : null;
  const ownerActorLoaded = owner === "me" ? actorLoaded : true;

  useEffect(() => { setBreadcrumbs([{ label: "Employee Initiatives" }]); return () => setBreadcrumbs([]); }, [setBreadcrumbs]);
  useEffect(() => {
    if (!management || tab !== "attention" || detailId !== null) return;
    const controller = new AbortController();
    setOwnerOptionsError("");
    getApi<{ id: number; fullName: string }[]>(GROWTH_ACCESS_EMPLOYEES, getAuthToken("accessToken"), "json", controller.signal)
      .then(({ data }) => {
        if (!Array.isArray(data)) throw new Error("Could not load owner names.");
        if (!controller.signal.aborted) setOwnerOptions(data.filter((item) => Number.isInteger(item.id) && item.fullName?.trim()).map((item) => ({ value: String(item.id), label: item.fullName.trim() })));
      })
      .catch((cause) => { if (!controller.signal.aborted) setOwnerOptionsError(cause instanceof Error ? cause.message : "Could not load owner names."); });
    return () => controller.abort();
  }, [management, tab, detailId, ownerOptionsAttempt]);
  useEffect(() => {
    const controller = new AbortController();
    getApi<{ data?: { personalDetail?: { id?: number } } }>(USER_PROFILE, getAuthToken("accessToken"), "json", controller.signal)
      .then(({ data }) => { if (!controller.signal.aborted) setActorId(data?.data?.personalDetail?.id ?? null); })
      .catch(() => { if (!controller.signal.aborted) setActorId(null); })
      .finally(() => { if (!controller.signal.aborted) setActorLoaded(true); });
    return () => controller.abort();
  }, []);
  useEffect(() => {
    if (!management) return;
    const controller = new AbortController();
    const token = getAuthToken("accessToken");
    setStatusCounts({});
    setTabCounts((counts) => ({ ...counts, attention: null }));
    void getApi<InitiativePage>(`${GROWTH_ALL_REQUESTS}?page=0&size=1`, token, "json", controller.signal)
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        const counts = Object.fromEntries(statusCards.map((card) => {
          const count = card.value === "" ? data.totalElements : data.statusCounts?.[card.value];
          return [card.value, typeof count === "number" && Number.isInteger(count) ? count : null];
        }));
        setStatusCounts(counts);
        setTabCounts((previous) => ({ ...previous, attention: counts.RECEIVED }));
      })
      .catch(() => { if (!controller.signal.aborted) setStatusCounts({}); });
    return () => controller.abort();
  }, [management, attempt]);
  useEffect(() => {
    const controller = new AbortController();
    setAssignedLoading(true); setAssignedError("");
    async function loadAssigned() {
      try {
        const token = getAuthToken("accessToken");
        if (!token) throw new Error("Your session has expired. Please sign in again.");
        const rows: Initiative[] = [];
        let totalPages = 1;
        let totalElements = 0;
        for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
          const params = new URLSearchParams({ page: String(pageIndex), size: "100" });
          if (employee) params.set("employee_id", employee);
          const response = await getApi<InitiativePage>(`${GROWTH_ASSIGNED_REQUESTS}?${params}`, token, "json", controller.signal);
          if (!Array.isArray(response.data?.content) || !Number.isInteger(response.data.totalPages) || response.data.totalPages < 0 || !Number.isInteger(response.data.totalElements)) throw new Error("Could not load employee initiatives.");
          rows.push(...response.data.content);
          totalPages = response.data.totalPages;
          totalElements = response.data.totalElements;
          if (controller.signal.aborted) return;
        }
        if (!controller.signal.aborted) {
          setAssignedRequests([...new Map(rows.map((row) => [row.id, row])).values()]);
          setTabCounts((counts) => ({ ...counts, assigned: totalElements }));
        }
      } catch (cause) { if (!controller.signal.aborted) setAssignedError(cause instanceof Error ? cause.message : "Could not load employee initiatives."); }
      finally { if (!controller.signal.aborted) setAssignedLoading(false); }
    }
    void loadAssigned();
    return () => controller.abort();
  }, [employee, attempt]);
  useEffect(() => {
    if (!management) return;
    if (dateRange === "custom" && (!fromDate || !toDate)) { setRequests([]); setError(""); setLoading(false); return; }
    if (owner === "me" && ownerActorId === null) {
      setRequests([]);
      setError(ownerActorLoaded ? "Could not identify the signed-in employee. Please refresh and try again." : "");
      setLoading(!ownerActorLoaded);
      return;
    }
    const controller = new AbortController();
    setLoading(true); setError(""); setRequests([]);
    async function load() {
      try {
        const token = getAuthToken("accessToken");
        if (!token) throw new Error("Your session has expired. Please sign in again.");
        const params = new URLSearchParams({ size: "100" });
        if (employee) params.set("employee_id", employee);
        if (category) params.set("growth_category", category);
        if (status) params.set("growth_status", status);
        if (fromDate && toDate) { params.set("from_date", fromDate); params.set("to_date", toDate); }
        if (owner === "unassigned") params.set("unassigned", "true");
        else if (owner === "me" && ownerActorId !== null) params.set("assigned_to", String(ownerActorId));
        else if (owner) params.set("assigned_to", owner);
        const rows: Initiative[] = [];
        let totalPages = 1;
        for (let index = 0; index < totalPages; index++) {
          params.set("page", String(index));
          const response = await getApi<InitiativePage>(`${GROWTH_ALL_REQUESTS}?${params}`, token, "json", controller.signal);
          if (!Array.isArray(response.data?.content) || !Number.isInteger(response.data.totalPages) || response.data.totalPages < 0) throw new Error("Could not load employee initiatives.");
          rows.push(...response.data.content);
          totalPages = response.data.totalPages;
          if (controller.signal.aborted) return;
        }
        if (!controller.signal.aborted) setRequests([...new Map(rows.map((row) => [row.id, row])).values()]);
      } catch (cause) { if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : "Could not load employee initiatives."); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    }
    void load();
    return () => controller.abort();
  }, [management, category, status, employee, owner, ownerActorId, ownerActorLoaded, fromDate, toDate, dateRange, attempt, cardRefresh]);

  const activeRequests = apiTab === "assigned" ? assignedRequests : requests;
  const activeLoading = apiTab === "assigned" ? assignedLoading : loading;
  const activeError = apiTab === "assigned" ? assignedError : error;
  const matchesFilters = (request: Initiative) => {
    if ((!management || apiTab === "assigned") && category && request.growthCategory !== category) return false;
    if ((!management || apiTab === "assigned") && status && request.growthStatus !== status) return false;
    if ((!management || apiTab === "assigned") && fromDate && toDate) {
      const created = request.createdAt?.slice(0, 10);
      if (!created || created < fromDate || created > toDate) return false;
    }
    return true;
  };
  const filtered = activeRequests.filter(matchesFilters);
  const safePage = Math.min(page, Math.max(1, Math.ceil(filtered.length / pageSize)));
  const visible = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);
  function filter(change: () => void) { change(); setPage(1); }
  function selectTab(next: View) { filter(() => { setTab(next); if (management) { setStatus(next === "attention" ? "RECEIVED" : ""); setEmployee(""); setOwner(""); } }); }
  function selectStatusCard(nextStatus: string) {
    setTab("attention"); setStatus(nextStatus); setCategory(""); setEmployee(""); setOwner("");
    setDateRange(""); setStartDate(null); setEndDate(null); setPage(1);
    setCardRefresh((value) => value + 1);
  }
  function clearFilters() {
    setCategory(""); setStatus(management && tab === "attention" ? "RECEIVED" : "");
    setEmployee(""); setOwner(""); setDateRange(""); setStartDate(null); setEndDate(null); setPage(1);
  }

  return <PageShell>{detailId !== null ? <ManagerRequestDetail key={detailId} requestId={detailId} assignmentEnabled={management && apiTab === "attention"} onBack={() => { setDetailId(null); setAttempt((value) => value + 1); }} onUpdated={(updated) => { if (apiTab === "assigned") setAssignedRequests((items) => items.map((item) => item.id === updated.id ? { ...item, ...updated } : item)); else setRequests((items) => items.map((item) => item.id === updated.id ? { ...item, ...updated } : item)); }} /> : <>
    {management ? <div className={styles.statsGrid} aria-label="Initiative status summary">
      {statusCards.map(({ value, label, Icon, tone }) => <button key={value} type="button" className={`${styles.statCard} ${tab === "attention" && status === value ? styles.statCardActive : ""}`} aria-pressed={tab === "attention" && status === value} onClick={() => selectStatusCard(value)}>
        <span className={`${styles.statIcon} ${styles[`stat${tone}`]}`}><Icon size={18} aria-hidden="true" /></span>
        <span className={styles.statCopy}><span>{label}</span><strong>{statusCounts[value]?.toLocaleString() ?? "—"}</strong></span>
      </button>)}
    </div> : null}
    <section className={styles.panel} aria-labelledby="team-growth-title">
    <header className={styles.header}><div><h1 id="team-growth-title">Employee Initiatives</h1><p>Review new initiatives, coordinate next steps, and keep things moving.</p></div></header>
    {management ? <div className={styles.tabs} aria-label="Initiative views">
      <button type="button" aria-pressed={tab === "attention"} onClick={() => selectTab("attention")}>Needs Attention <span>{tabCounts.attention ?? "—"}</span></button>
      <button type="button" aria-pressed={tab === "assigned"} onClick={() => selectTab("assigned")}>Assigned to Me <span>{tabCounts.assigned ?? "—"}</span></button>
    </div> : null}
    <div className={management && tab === "attention" ? styles.filters : `${styles.filters} ${styles.teamFilters}`}>
      <div role="group" aria-label="Category filter"><Select id="team-growth-category" value={category} onChange={(value) => filter(() => setCategory(value))} options={[{ value: "", label: "All categories" }, ...categories.map(({ value, label }) => ({ value, label }))]} /></div>
      <div role="group" aria-label="Status filter"><Select id="team-growth-status" value={status} onChange={(value) => filter(() => setStatus(value))} options={[{ value: "", label: "All statuses" }, ...statuses]} /></div>
      <div role="group" aria-label="Employee filter"><Select id="team-growth-employee" searchable value={employee} onChange={(value) => filter(() => setEmployee(value))} options={[{ value: "", label: "All employees" }, ...employees.options]} /></div>
      {management && tab === "attention" ? <div role="group" aria-label="Owner filter"><Select id="team-growth-owner" searchable value={owner} onChange={(value) => filter(() => setOwner(value))} options={[{ value: "", label: "All owners" }, { value: "me", label: "Assigned to me", disabled: actorId === null }, { value: "unassigned", label: "Unassigned" }, ...ownerOptions]} /></div> : null}
      <div role="group" aria-label="Date filter"><Select id="team-growth-date" value={dateRange} onChange={(value) => filter(() => { setDateRange(value); if (value !== "custom") { setStartDate(null); setEndDate(null); } })} options={[{ value: "", label: "Any date" }, { value: "7", label: "Past 7 days" }, { value: "30", label: "Past 30 days" }, { value: "custom", label: "Custom range" }]} /></div>
      <button className={styles.clearFilters} type="button" onClick={clearFilters}><RotateCcw size={15} aria-hidden="true" />Clear Filters</button>
      {dateRange === "custom" ? <div className={styles.customDate}><DateRange id="growth-custom-dates" label="From date to date" startDate={startDate} endDate={endDate} maxDate={new Date()} onChange={([start, end]) => filter(() => { setStartDate(start); setEndDate(end); })} placeholderText="Select start and end dates" />{!startDate || !endDate ? <small>Select both dates to show initiatives.</small> : null}</div> : null}
    </div>
    {employees.error ? <p className={styles.lookupError} role="alert">Employee names could not be loaded. <button type="button" onClick={() => void employees.refetch()}>Retry</button></p> : null}
    {ownerOptionsError && management && tab === "attention" ? <p className={styles.lookupError} role="alert">{ownerOptionsError} <button type="button" onClick={() => setOwnerOptionsAttempt((value) => value + 1)}>Retry owners</button></p> : null}
    {activeLoading ? <p className={styles.state} role="status">Loading employee initiatives…</p> : activeError ? <div className={styles.state}><p role="alert">{activeError}</p><Button onClick={() => setAttempt((value) => value + 1)}>Try again</Button></div> : <>
      {!visible.length ? <p className={styles.state}>{dateRange === "custom" && (!startDate || !endDate) ? "Select a start and end date." : activeRequests.length ? "No initiatives match this view and its filters." : "No employee initiatives found."}</p> : <ul className={styles.list}>{visible.map((request) => {
        const info = categories.find((item) => item.value === request.growthCategory);
        const Icon = info?.icon || Rocket;
        return <li key={request.id}><button className={styles.row} type="button" onClick={() => setDetailId(request.id)}>
          <span className={`${styles.icon} ${styles[info?.tone || "blue"]}`}><Icon size={21} aria-hidden /></span>
          <span className={styles.copy}><span className={styles.title}><strong>{request.displayTitle || info?.title || "Growth initiative"}</strong>{actionLabel(request.growthStatus) ? <small className={styles.action}>{actionLabel(request.growthStatus)}</small> : null}</span><span>{request.employeeName || "Employee name unavailable"}</span><small>{info?.label || request.growthCategory} · Shared {sharedDate(request.createdAt)}</small></span>
          <span className={styles.owner}><small>OWNER</small><span>{request.assignedToId === null ? <span className={styles.unassigned}>Being assigned</span> : request.assignedToName || ownerOptions.find((item) => item.value === String(request.assignedToId))?.label || employees.options.find((item) => item.value === String(request.assignedToId))?.label || (request.assignedToId === actorId ? "You" : "Assigned owner")}</span></span>
          <span className={styles.status}><small>STATUS</small><span className={`${styles.statusPill} ${styles[request.growthStatus] || ""}`}>{statuses.find((item) => item.value === request.growthStatus)?.label || request.growthStatus}</span></span>
          <ArrowRight size={17} className={styles.arrow} aria-hidden />
        </button></li>;
      })}</ul>}
      {filtered.length > 0 ? <TablePagination className={styles.pagination} page={safePage} pageSize={pageSize} totalItems={filtered.length} onPageChange={setPage} onPageSizeChange={(value) => filter(() => setPageSize(value))} /> : null}
    </>}
  </section></>}{activeLoading && detailId === null ? <Spinner variant="overlay" label="Loading employee initiatives" /> : null}</PageShell>;
}
